
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FloorplanSection = () => {
  const navigate = useNavigate();
  const [showPrequalForm, setShowPrequalForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleCTAClick = (context: string, cta: string) => {
    // Analytics event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'cta_click', {
        context: context,
        cta: cta
      });
    }
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      const h = document.querySelector('.site-header')?.getBoundingClientRect().height || 80;
      const y = contactSection.getBoundingClientRect().top + window.pageYOffset - h;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handlePurchaseClick = () => {
    navigate('/purchase');
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Convert FormData to URLSearchParams for application/x-www-form-urlencoded
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d3gpq3n09ffggnll76ig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => {
          setShowPrequalForm(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="floorplan" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Floorplan Financing — Stock Inventory Without Tying Up Cash
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Floorplan financing allows new Georgia dealers to stock inventory without large upfront capital. 
              Learn how to secure credit lines for vehicle purchases and manage cash flow effectively.
            </p>
          </div>

          {/* Requirements Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                alt="Car dealership inventory financing floorplan credit line management professional automotive business"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
                src="https://public.readdy.ai/ai/img_res/09dadcf1662e88d5c3d531edcdc1ae56.jpg"
                width="600"
                height="400"
                loading="lazy"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Requirements</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-yellow-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Valid business entity (LLC or Corporation)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-yellow-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Active Georgia dealer license</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-yellow-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">Federal EIN and business bank statements</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-yellow-600 text-sm"></i>
                  </div>
                  <span className="text-gray-7">Detailed inventory acquisition plan</span>
                </li>
              </ul>
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">How It Works</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Apply</h4>
                <p className="text-gray-600">
                  Submit application with business documents, financial statements, and inventory plan
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">2</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Approval & Limit</h4>
                <p className="text-gray-600">
                  Receive credit approval and establish your floorplan credit limit based on qualifications
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">3</span>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">Buy & Repay</h4>
                <p className="text-gray-600">
                  Purchase inventory using credit line, then repay as units sell with interest
                </p>
              </div>
            </div>
          </div>

          {/* Mini Grid - Financing Details */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-time-line text-blue-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Time in Business</h4>
              <p className="text-sm text-gray-600">Typically 6+ months operating history required</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-money-dollar-circle-line text-green-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Line Size Range</h4>
              <p className="text-sm text-gray-600">$50K - $2M+ depending on qualifications</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-percent-line text-yellow-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Fees & Interest</h4>
              <p className="text-sm text-gray-600">Competitive rates based on credit profile</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-calendar-check-line text-purple-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Audit Cadence</h4>
              <p className="text-sm text-gray-600">Monthly reporting and periodic audits</p>
            </div>
          </div>

          {/* CTAs */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://drive.google.com/file/d/15mwTL1E347Xo6zRAAb_miL0HbDsxiOHn/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-50 hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Browse All Resources
                <i className="ri-external-link-line ml-2"></i>
              </a>
              
              <button
                onClick={() => window.open('https://www.georgiadealerlicense.com/purchase', '_blank')}
                className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Get Complete Access - $97
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Pre-qualification Modal */}
      {showPrequalForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Floorplan Pre-Qualification</h3>
              <button
                onClick={() => setShowPrequalForm(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            {submitStatus === 'success' && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-600 mr-2"></i>
                  <span className="text-green-800 font-medium">Pre-qualification submitted successfully!</span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 mr-2"></i>
                  <span className="text-red-800 font-medium">Submission failed. Please try again.</span>
                </div>
              </div>
            )}
            
            <form 
              id="floorplan-prequalification-form"
              data-readdy-form
              className="space-y-4" 
              onSubmit={handleFormSubmit}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                <input
                  type="text"
                  name="business_name"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="Your dealership name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Revenue *</label>
                <select 
                  name="monthly_revenue"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent pr-8"
                >
                  <option value="">Select range</option>
                  <option value="10k-25k">$10K - $25K</option>
                  <option value="25k-50k">$25K - $50K</option>
                  <option value="50k-100k">$50K - $100K</option>
                  <option value="100k+">$100K+</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Desired Credit Line *</label>
                <select 
                  name="desired_credit_line"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent pr-8"
                >
                  <option value="">Select amount</option>
                  <option value="50k-100k">$50K - $100K</option>
                  <option value="100k-250k">$100K - $250K</option>
                  <option value="250k-500k">$250K - $500K</option>
                  <option value="500k+">$500K+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time in Business</label>
                <select 
                  name="time_in_business"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent pr-8"
                >
                  <option value="">Select timeframe</option>
                  <option value="startup">Startup (0-6 months)</option>
                  <option value="6-12months">6-12 months</option>
                  <option value="1-2years">1-2 years</option>
                  <option value="2+years">2+ years</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                <textarea
                  name="additional_info"
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Tell us about your inventory needs, business goals, or any questions..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 rounded-lg font-bold transition-all cursor-pointer whitespace-nowrap ${
                  isSubmitting 
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:from-yellow-500 hover:to-yellow-600'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting...
                  </>
                ) : (
                  'Submit Pre-Qualification'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default FloorplanSection;
